import User from "@/modules/user";

const utmPrefix = "REMOVE_BG_TO_CANVA";
const utmMedium = "acquisitions";
const utmSource = "removebg";

// These are our unique internal CTA identifiers
export enum CanvaCTAIdentifier {
  ERASE_DISTRACTIONS = "erase-distractions",
  ERASE_DISTRACTIONS_VISUAL = "erase-distractions-visual",
  ADD_FILTERS = "add-filters",
  ADD_FILTERS_VISUAL = "add-filters-visual",
  COLOR_AND_LIGHT = "color-and-light",
  CROP_AND_PLACE = "crop-and-place",
  CROP_AND_PLACE_VISUAL = "crop-and-place-visual",
  EXPAND_IMAGE = "expand-image",
  ADD_TEXT = "add-text",
  ADD_TEXT_VISUAL = "add-text-visual",
  ADD_ELEMENTS = "add-elements",
  USE_TEMPLATE = "use-template",
}

export enum CanvaVariant {
  DEFAULT = "DEFAULT", // A
  WITH_DOCTYPE = "WITH_DOCTYPE", // B
  WITH_DOCTYPE_AND_BRANDING = "WITH_DOCTYPE_AND_BRANDING", // C
  INLINE_EDITOR_DEFAULT = "INLINE_EDITOR_DEFAULT", // IA
  INLINE_EDITOR_ANDROID_APP_DEFAULT = "INLINE_EDITOR_ANDROID_APP_DEFAULT", // IAM
}

// These are the onboarding flows on Canva's side
enum CanvaOnboardingFlow {
  DEFAULT = "editor", // opens Adjust panel within the Image Editor Panel
  IMAGE_EDIT = "image-editor-panel", // opens Image Editor Panel
  CROP_PHOTO = "crop-image",
  EXPAND_IMAGE = "image-expander",
  ADD_TEXT = "add-text-to-photo",
  MAGIC_ERASER = "inpainting-effect",
}

export function useCanvaLinkBuilder(flavor: string) {
  const base = flavor === "production" ? "https://canva.com/content-partner" : "https://canva-dev.com/content-partner";

  // TODO: when removing the legacy flow, we can make the identifier param required
  const buildUrl = (image: string, variant?: CanvaVariant, identifier?: CanvaCTAIdentifier) => {
    const url = new URL(base);

    url.searchParams.append("image-url", image);

    const variantParam = getVariantParam(variant);
    url.searchParams.append("variant", variantParam);
    url.searchParams.append("utm_campaign", `${utmPrefix}_${variantParam}`);
    url.searchParams.append("utm_medium", utmMedium);
    url.searchParams.append("utm_source", utmSource);

    if (identifier) {
      url.searchParams.append("utm_content", identifier);
    }

    const onboardingFlow = getOnboardingFlowParam(identifier);
    if (onboardingFlow) {
      url.searchParams.append("onboarding-flow", onboardingFlow);
    }

    return url.toString();
  };

  const getVariantParam = (variant?: CanvaVariant) => {
    if (!variant) {
      return CanvaVariant.INLINE_EDITOR_DEFAULT;
    }

    if (User.mobileApp() && variant === CanvaVariant.INLINE_EDITOR_DEFAULT) {
      return CanvaVariant.INLINE_EDITOR_ANDROID_APP_DEFAULT;
    }

    return variant;
  };

  const getOnboardingFlowParam = (ctaIdentifier: CanvaCTAIdentifier): CanvaOnboardingFlow | null => {
    const mapping = new Map<CanvaCTAIdentifier, CanvaOnboardingFlow>([
      // Buttons
      [CanvaCTAIdentifier.ERASE_DISTRACTIONS, CanvaOnboardingFlow.MAGIC_ERASER],
      [CanvaCTAIdentifier.ADD_FILTERS, CanvaOnboardingFlow.IMAGE_EDIT],
      [CanvaCTAIdentifier.COLOR_AND_LIGHT, CanvaOnboardingFlow.DEFAULT],
      [CanvaCTAIdentifier.CROP_AND_PLACE, CanvaOnboardingFlow.CROP_PHOTO],
      [CanvaCTAIdentifier.EXPAND_IMAGE, CanvaOnboardingFlow.EXPAND_IMAGE],
      [CanvaCTAIdentifier.ADD_TEXT, CanvaOnboardingFlow.ADD_TEXT],

      // Visuals
      [CanvaCTAIdentifier.ERASE_DISTRACTIONS_VISUAL, CanvaOnboardingFlow.MAGIC_ERASER],
      [CanvaCTAIdentifier.ADD_FILTERS_VISUAL, CanvaOnboardingFlow.IMAGE_EDIT],
      [CanvaCTAIdentifier.CROP_AND_PLACE_VISUAL, CanvaOnboardingFlow.CROP_PHOTO],
      [CanvaCTAIdentifier.ADD_TEXT_VISUAL, CanvaOnboardingFlow.ADD_TEXT],

      // No onboarding flow for these
      [CanvaCTAIdentifier.ADD_ELEMENTS, null],
      [CanvaCTAIdentifier.USE_TEMPLATE, null],
    ]);

    return mapping.get(ctaIdentifier) ?? null;
  };

  return {
    buildUrl,
  };
}
